import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeaderProps } from '../../types';
import Navigation from '../molecules/Navigation';
import Icon from '../atoms/Icon';
import { CartIcon } from '../atoms/CartIcon';
import { CartDrawer } from './CartDrawer';
import SearchOverlay from '../molecules/SearchOverlay';
import './Header.css';

const Header: React.FC<HeaderProps> = ({ navLinks = [] }) => {
  const navigate = useNavigate();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleCartClick = () => {
    setIsCartOpen(true);
  };

  const handleCartClose = () => {
    setIsCartOpen(false);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSearchClick = () => {
    setIsSearchOpen(true);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.header') && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="header">
        <div className="header-container">
          {/* Mobile hamburger menu button */}
          <div className="mobile-menu-toggle">
            <button 
              className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={handleMobileMenuToggle}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
              type="button"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>

          {/* Desktop navigation */}
          <div className="header-left desktop-nav">
            <Navigation links={navLinks} />
          </div>
          
          <div className="header-center">
            <button 
              className="logo-container"
              onClick={() => navigate('/')}
              aria-label="Go to The Frosting Room homepage"
              type="button"
            >
              <img 
                src="/images/logo/tfr.avif" 
                alt="The Frosting Room Logo" 
                className="logo-image"
              />
            </button>
          </div>
          
          <div className="header-right">
            <div className="header-icons">
              <button 
                className="search-icon-button" 
                onClick={handleSearchClick}
                aria-label="Search products"
              >
                <Icon type="search" size="medium" />
              </button>
              <div className="cart-icon-wrapper">
                <CartIcon onClick={handleCartClick} />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile navigation menu */}
        <div id="mobile-navigation" className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
          <Navigation 
            links={navLinks} 
            onLinkClick={handleMobileMenuClose}
            isMobile={true}
          />
        </div>
      </header>
      
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={handleCartClose}
      />
      
      <SearchOverlay 
        isOpen={isSearchOpen} 
        onClose={handleSearchClose}
      />
    </>
  );
};

export default Header;
